/**
 * APA 7 headings from a typed outline.
 *
 * The five levels (APA Publication Manual, 7th ed., §2.27, pp. 47–49; confirmed against
 * university guides that reproduce APA's own table):
 *
 * | Level | Format                                                                  |
 * | ----- | ----------------------------------------------------------------------- |
 * | 1     | Centered, Bold, Title Case Heading                                      |
 * | 2     | Flush Left, Bold, Title Case Heading                                    |
 * | 3     | Flush Left, Bold Italic, Title Case Heading                             |
 * | 4     | Indented, Bold, Title Case Heading, Ending With a Period. Text follows. |
 * | 5     | Indented, Bold Italic, Title Case Heading, Ending With a Period. Text … |
 *
 * And the advice the warnings repeat: use the levels in order from Level 1; a section
 * divided into subsections has at least two of them; headings are not numbered; and
 * the opening of a paper takes no "Introduction" heading, because the paper's title at
 * the top of the first page works as its Level 1 heading.
 */

import { escapeHtml } from "@/lib/tool-kit/citation-parts";
import {
  apaTitleCase,
  isEnglishLocale,
} from "@/lib/tool-kit/paper-formatting/apa-title-case";
import { parseOutline } from "@/lib/tool-kit/paper-formatting/outline";

export type ApaLevel = 1 | 2 | 3 | 4 | 5;

export interface ApaHeading {
  line: number;
  level: ApaLevel;
  /** The heading as it should appear: cased, numbered or not, punctuated. */
  text: string;
  /** What the writer typed, without marks. */
  typed: string;
}

export type HeadingWarningKind =
  | "first-not-level-1"
  | "skipped-level"
  | "single-subsection"
  | "introduction"
  | "numbered"
  | "too-deep";

export interface HeadingWarning {
  kind: HeadingWarningKind;
  line: number;
  values: Record<string, string | number>;
}

export interface HeadingOptions {
  titleCase: boolean;
  /** Keep outline numbers such as "2.1" in the heading text. APA does not. */
  keepNumbers: boolean;
  /**
   * The page's language. English title case and the "Introduction" warning apply only to
   * English; any other language keeps the writer's capitalisation. Omitted means English.
   */
  locale?: string;
}

export interface HeadingResult {
  headings: ApaHeading[];
  warnings: HeadingWarning[];
  /** The outline carried numbers, and they were left out. */
  numbersRemoved: boolean;
}

const RE_TRAILING_STOP = /[.:]+$/;
const RE_TRAILING_COLON = /:$/;
const RE_ENDS_IN_PUNCT = /[.?!]["”’)]*$/;
const RE_INTRODUCTION = /^introduction$/i;

function finishText(text: string, level: ApaLevel): string {
  const bare = text.trim();
  if (level <= 3) {
    return bare.replace(RE_TRAILING_STOP, "");
  }
  return RE_ENDS_IN_PUNCT.test(bare)
    ? bare
    : `${bare.replace(RE_TRAILING_COLON, "")}.`;
}

function structureWarnings(headings: readonly ApaHeading[]): HeadingWarning[] {
  const warnings: HeadingWarning[] = [];
  const first = headings[0];
  if (first && first.level > 1) {
    warnings.push({
      kind: "first-not-level-1",
      line: first.line,
      values: { heading: first.text, level: first.level },
    });
  }
  for (let i = 1; i < headings.length; i++) {
    const current = headings[i];
    const previous = headings[i - 1];
    if (current.level > previous.level + 1) {
      warnings.push({
        kind: "skipped-level",
        line: current.line,
        values: {
          heading: current.text,
          level: current.level,
          previous: previous.level,
          expected: previous.level + 1,
        },
      });
    }
  }
  return warnings;
}

/** A heading with exactly one subsection one level down. */
function singleSubsectionWarnings(
  headings: readonly ApaHeading[]
): HeadingWarning[] {
  const warnings: HeadingWarning[] = [];
  for (let i = 0; i < headings.length; i++) {
    const parent = headings[i];
    const children: ApaHeading[] = [];
    for (let j = i + 1; j < headings.length; j++) {
      if (headings[j].level <= parent.level) {
        break;
      }
      if (headings[j].level === parent.level + 1) {
        children.push(headings[j]);
      }
    }
    if (children.length === 1) {
      warnings.push({
        kind: "single-subsection",
        line: children[0].line,
        values: {
          heading: children[0].text,
          parent: parent.text,
          level: children[0].level,
        },
      });
    }
  }
  return warnings;
}

/** Turn a typed outline into APA headings, with warnings about its structure. */
export function buildApaHeadings(
  input: string,
  options: HeadingOptions
): HeadingResult {
  const warnings: HeadingWarning[] = [];
  let numbersRemoved = false;
  const english = isEnglishLocale(options.locale);
  const headings: ApaHeading[] = parseOutline(input).map((line) => {
    if (line.level > 5) {
      warnings.push({
        kind: "too-deep",
        line: line.line,
        values: { heading: line.text, level: line.level },
      });
    }
    const level = Math.min(Math.max(line.level, 1), 5) as ApaLevel;
    const cased =
      options.titleCase && english ? apaTitleCase(line.text) : line.text;
    let text = cased;
    if (line.number && options.keepNumbers) {
      text = `${line.number} ${cased}`;
      warnings.push({
        kind: "numbered",
        line: line.line,
        values: { heading: text },
      });
    } else if (line.number) {
      numbersRemoved = true;
    }
    return {
      line: line.line,
      level,
      text: finishText(text, level),
      typed: line.text,
    };
  });
  warnings.push(...structureWarnings(headings));
  warnings.push(...singleSubsectionWarnings(headings));
  const first = headings[0];
  if (
    english &&
    first &&
    RE_INTRODUCTION.test(first.typed.replace(RE_TRAILING_STOP, ""))
  ) {
    warnings.push({
      kind: "introduction",
      line: first.line,
      values: { heading: first.text },
    });
  }
  warnings.sort((a, b) => a.line - b.line);
  return { headings, warnings, numbersRemoved };
}

// --- output ------------------------------------------------------------------------

const PARAGRAPH = "margin:0;line-height:2";

/**
 * Word- and Google Docs-ready HTML: one paragraph per heading, formatted with inline
 * styles and <strong>/<em>, so the paste keeps alignment, indent, bold and italics.
 * Levels 4 and 5 end with a space, ready for the paragraph's first sentence.
 */
export function headingsToHtml(headings: readonly ApaHeading[]): string {
  return headings
    .map(({ level, text }) => {
      const safe = escapeHtml(text);
      switch (level) {
        case 1:
          return `<p style="${PARAGRAPH};text-align:center"><strong>${safe}</strong></p>`;
        case 2:
          return `<p style="${PARAGRAPH}"><strong>${safe}</strong></p>`;
        case 3:
          return `<p style="${PARAGRAPH}"><strong><em>${safe}</em></strong></p>`;
        case 4:
          return `<p style="${PARAGRAPH};text-indent:0.5in"><strong>${safe}</strong> </p>`;
        default:
          return `<p style="${PARAGRAPH};text-indent:0.5in"><strong><em>${safe}</em></strong> </p>`;
      }
    })
    .join("");
}

/** Plain text: one heading per line, Levels 4 and 5 indented with a tab. */
export function headingsToText(headings: readonly ApaHeading[]): string {
  return headings
    .map(({ level, text }) => (level >= 4 ? `\t${text}` : text))
    .join("\n");
}
